// In-app KPI card with three states: loading / empty / data.
// See docs/design-system/guidelines/dashboards.md for usage rules.

import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { InfoTooltip } from "@/components/ui/info-tooltip";
import { Sparkline } from "@/components/report/Sparkline";
import { CountUp } from "@/components/motion/CountUp";

/**
 * Visual weight of the card. Drives the value type-scale, padding, and
 * sparkline height so the KPI grid can render a "hero + demote" hierarchy
 * from one component instead of two:
 *   - "hero"    — the 1–2 headline metrics. Larger value, taller card +
 *                 sparkline, meant to span multiple grid columns.
 *   - "compact" — the demoted strip. Smaller value + sparkline, denser.
 * Defaults to "compact" so existing callers keep their current density.
 */
export type MetricCardSize = "hero" | "compact";

interface BaseProps {
  label: string;
  className?: string;
  /** Visual weight — see {@link MetricCardSize}. Default "compact". */
  size?: MetricCardSize;
  /**
   * Optional tooltip content for an info (i) icon rendered next to the
   * label. When omitted, no icon is rendered. Used by the Overview KPIs
   * to explain what each metric represents.
   */
  info?: ReactNode;
}

interface LoadingProps extends BaseProps {
  state: "loading";
}

interface EmptyProps extends BaseProps {
  state: "empty";
  emptyMessage: string;
  /** Single word to italicize in Quarto inside `emptyMessage` (e.g. "arriving"). */
  emptyAccent?: string;
}

interface DataProps extends BaseProps {
  state: "data";
  value: number;
  /** Decimal delta (e.g. 0.184 for +18.4%). null = no comparison available.
   *  When `deltaUnit` is "pp", this is instead the raw decimal
   *  percentage-point delta (e.g. 0.228 for +22.8 pp). */
  delta?: number | null;
  /** How to render the delta:
   *  - "pct" (default) renders as relative percent: "+18.4%"
   *  - "pp" renders as percentage points: "+22.8 pp"
   *  Use "pp" for ratio/percentage KPIs (engagement rate, social share,
   *  conversion rate) so readers don't conflate a relative change with
   *  an absolute one. Use "pct" for count metrics (sessions, events). */
  deltaUnit?: "pct" | "pp";
  /** Optional formatter for the value; defaults to `Intl.NumberFormat`. */
  format?: (n: number) => string;
  /**
   * Optional per-bucket daily series for this metric, oldest → newest.
   * When present (≥2 points), an `aria-hidden` sparkline renders below
   * the value/delta. Decorative only — the value + delta already carry
   * the semantics. Omit to render no chart.
   */
  spark?: number[];
}

export type MetricCardProps = LoadingProps | EmptyProps | DataProps;

const baseCardClass = "flex flex-col rounded-xl bg-white ring-1 ring-neutral-950/5";
// Hero cards get more breathing room + a taller floor so they read as the
// headline pair; compact cards stay dense for the demoted strip.
const sizeCardClass: Record<MetricCardSize, string> = {
  hero: "min-h-[176px] p-6",
  compact: "min-h-[132px] p-5",
};

export function MetricCard(props: MetricCardProps) {
  const size = props.size ?? "compact";
  return (
    <article
      className={cn(baseCardClass, sizeCardClass[size], props.className)}
      aria-busy={props.state === "loading"}
    >
      <div className="flex items-center gap-1.5">
        <p className="truncate text-[11px] font-bold uppercase tracking-wide text-neutral-500">
          {props.label}
        </p>
        {props.info ? <InfoTooltip label={props.label}>{props.info}</InfoTooltip> : null}
      </div>
      {renderBody(props, size)}
    </article>
  );
}

function renderBody(props: MetricCardProps, size: MetricCardSize) {
  if (props.state === "loading") {
    const barWidth = size === "hero" ? "h-11 w-40" : "h-8 w-32";
    return (
      <>
        <div
          aria-hidden="true"
          className={cn("mt-4 animate-pulse rounded bg-neutral-100", barWidth)}
        />
        <div aria-hidden="true" className="mt-3 h-3 w-40 animate-pulse rounded bg-neutral-100" />
        <span className="sr-only">Loading {props.label}…</span>
      </>
    );
  }

  if (props.state === "empty") {
    return (
      <>
        <div className="mt-3 flex items-center gap-2">
          <span aria-hidden="true" className="size-2 shrink-0 rounded-full bg-purple-500" />
          <p className="text-sm leading-snug text-neutral-700 text-pretty">
            {renderEmptyMessage(props)}
          </p>
        </div>
      </>
    );
  }

  const formatter = props.format ?? defaultFormat;
  const valueClass =
    size === "hero"
      ? "font-display text-5xl font-bold tabular-nums leading-none text-neutral-950"
      : "font-display text-3xl font-bold tabular-nums leading-none text-neutral-950";
  const hasSpark = Array.isArray(props.spark) && props.spark.length >= 2;
  const sparkHeight = size === "hero" ? "h-10" : "h-7";
  return (
    <>
      <p className={cn("mt-3", valueClass)}>
        {/* CountUp animates 0 → value on mount and prev → new whenever the
            value changes (date range / comparison / granularity), which is the
            "data changed" feedback for the dashboard. It renders the SAME
            formatted string at rest via `format`, so tabular-nums / %, /
            currency formatting is preserved. ~0.7s ease-out; reduced motion
            snaps to the final value (guarded inside CountUp). */}
        <CountUp value={props.value} format={formatter} duration={0.7} />
      </p>
      {props.delta !== undefined && props.delta !== null ? (
        <p className="mt-3">
          <DeltaPill delta={props.delta} deltaUnit={props.deltaUnit ?? "pct"} />
        </p>
      ) : null}
      {hasSpark ? (
        // Pushed to the bottom of the card so the value/delta stay
        // top-aligned across the grid and the trend reads as a baseline.
        <div className={cn("mt-auto w-full pt-3", sparkHeight)}>
          <Sparkline points={props.spark!} className="h-full w-full" />
        </div>
      ) : null}
    </>
  );
}

function renderEmptyMessage(props: EmptyProps) {
  if (!props.emptyAccent) return props.emptyMessage;
  const idx = props.emptyMessage.indexOf(props.emptyAccent);
  if (idx === -1) return props.emptyMessage;
  const before = props.emptyMessage.slice(0, idx);
  const after = props.emptyMessage.slice(idx + props.emptyAccent.length);
  return (
    <>
      {before}
      <em className="font-serif font-semibold text-purple-700">{props.emptyAccent}</em>
      {after}
    </>
  );
}

function DeltaPill({ delta, deltaUnit }: { delta: number; deltaUnit: "pct" | "pp" }) {
  const positive = delta >= 0;
  const label =
    deltaUnit === "pp"
      ? `${positive ? "+" : ""}${(delta * 100).toFixed(1)} pp`
      : `${positive ? "+" : ""}${(delta * 100).toFixed(1)}%`;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums",
        positive ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700",
      )}
    >
      <i
        aria-hidden="true"
        className={cn("fa-solid text-[10px]", positive ? "fa-arrow-up" : "fa-arrow-down")}
      />
      {label}
    </span>
  );
}

const defaultFormat = (n: number) =>
  new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(n);
